/**
 * Creates a debounced function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was invoked.
 * @param func The function to debounce.
 * @param wait The number of milliseconds to which invocations are debounced.
 * @param options Specify invoking on the `leading` or `trailing` edge of the timeout (Default is `trailing=true`).
 * @category Function
 * @returns A new debounced function paired with a cancel function.
 * @example
function calculateLayout() {}
const [debounced, cancel] = debounce(calculateLayout, 100)
window.addEventListener('resize', debounced)
cancel()
 */
export default function debounce(
	func,
	wait = 0,
	{ leading = false, trailing = true } = {},
) {
	const name = `debounce(${func.name})`
	let timer
	const debounced = {
		[name]: (...args) => {
			if (!wait) {
				return func(...args)
			}
			const callNow = leading && !timer
			cancel()
			timer = setTimeout(() => {
				timer = null
				if (trailing) {
					return func(...args)
				}
			}, wait)
			if (callNow) {
				return func(...args)
			}
		},
	}[name]
	return [debounced, cancel]
	/**
	 * Cancels delayed `func` invocations.
	 */
	function cancel() {
		clearTimeout(timer)
	}
}
